import React from 'react';
import { NodeWrapper, TextNode, ImageNode, IframeNode } from '../nodes';
import { SlideContainer } from '../SlideContainer';
import type { Slide, Node, TextNodeData } from '../../types';

interface SlideCanvasProps {
  slides: Slide[];
  slideNodesCache: Record<string, Node[]>;
  currentSlideIndex: number;
  isEditing: boolean;
  animatingNodeIds: Set<string>;
  activeNodeId: string | null;
  setActiveNodeId: (id: string | null) => void;
  nodeOps: {
    bringToFront: (id: string) => void;
    updateNode: (id: string, updates: any) => void;
    updateNodeData: (id: string, data: any) => void;
    deleteNode: (id: string) => Promise<void>;
    duplicateNode: (id: string) => Promise<void>;
    toggleFrameStyle: (id: string) => Promise<void>;
  };
}

export const SlideCanvas: React.FC<SlideCanvasProps> = ({
  slides,
  slideNodesCache,
  currentSlideIndex,
  isEditing,
  animatingNodeIds,
  activeNodeId,
  setActiveNodeId,
  nodeOps,
}) => {
  return (
    <div className="slide-canvas relative w-full h-full">
      {slides.map((slide, index) => {
        const isCurrentSlide = index === currentSlideIndex;
        const isAbove = index < currentSlideIndex;
        const isBelow = index > currentSlideIndex;
        const shouldShowAsLocked = slide.locked && index <= currentSlideIndex && (!slide.unlock_on_slide || currentSlideIndex < slide.unlock_on_slide);

        const isUnlocking = slide.locked && slide.unlock_on_slide !== undefined && currentSlideIndex >= slide.unlock_on_slide;

        return (
          <SlideContainer
            key={slide.id}
            slideId={slide.id}
            isCurrentSlide={isCurrentSlide}
            isAbove={isAbove}
            isBelow={isBelow}
            isLocked={shouldShowAsLocked}
            isUnlocking={isUnlocking}
            zIndex={slide.z_index}
          >
            {(slideNodesCache[slide.id] || []).map((node, nodeIndex) => (
              <NodeWrapper
                key={node.id}
                id={node.id}
                position={node.position}
                size={node.size}
                zIndex={node.zIndex}
                frameStyle={node.data.frameStyle}
                nodeType={node.type}
                isEditing={isCurrentSlide && isEditing}
                isActive={activeNodeId === node.id}
                backgroundColor={node.type === 'text' ? (node.data as TextNodeData).backgroundColor : undefined}
                workInProgress={'workInProgress' in node.data ? node.data.workInProgress : undefined}
                borderRadius={'borderRadius' in node.data ? node.data.borderRadius : undefined}
                iframeZoom={node.type === 'iframe' ? ('iframeZoom' in node.data ? node.data.iframeZoom : undefined) : undefined}
                isAnimatingIn={animatingNodeIds.has(node.id)}
                animationDelay={nodeIndex * 50}
                onActivate={() => {
                  setActiveNodeId(node.id);
                  if (isEditing) {
                    nodeOps.bringToFront(node.id);
                  }
                }}
                onPositionChange={(position) => isCurrentSlide && nodeOps.updateNode(node.id, { position })}
                onSizeChange={(size) => isCurrentSlide && nodeOps.updateNode(node.id, { size })}
                onDelete={() => isCurrentSlide && nodeOps.deleteNode(node.id)}
                onDuplicate={() => isCurrentSlide && nodeOps.duplicateNode(node.id)}
                onFrameToggle={() => isCurrentSlide && nodeOps.toggleFrameStyle(node.id)}
                onBackgroundColorChange={(color) => isCurrentSlide && node.type === 'text' && nodeOps.updateNodeData(node.id, { backgroundColor: color })}
                onWorkInProgressToggle={() => {
                  if (isCurrentSlide) {
                    const currentWip = 'workInProgress' in node.data ? node.data.workInProgress : false;
                    nodeOps.updateNodeData(node.id, { workInProgress: !currentWip });
                  }
                }}
                onBorderRadiusChange={(radius) => {
                  if (isCurrentSlide) {
                    nodeOps.updateNodeData(node.id, { borderRadius: radius });
                  }
                }}
                onIframeZoomChange={(zoom) => {
                  if (isCurrentSlide && node.type === 'iframe') {
                    nodeOps.updateNodeData(node.id, { iframeZoom: zoom });
                  }
                }}
              >
                {node.type === 'text' ? (
                  <TextNode
                    id={node.id}
                    content={node.data.content}
                    frameStyle={(node.data as TextNodeData).frameStyle}
                    style={'style' in node.data ? node.data.style : {}}
                    senderName={'senderName' in node.data ? node.data.senderName : undefined}
                    avatarUrl={'avatarUrl' in node.data ? node.data.avatarUrl : undefined}
                    avatarEmoji={'avatarEmoji' in node.data ? node.data.avatarEmoji : undefined}
                    backgroundColor={'backgroundColor' in node.data ? node.data.backgroundColor : undefined}
                    workInProgress={'workInProgress' in node.data ? node.data.workInProgress : undefined}
                    borderRadius={'borderRadius' in node.data ? node.data.borderRadius : undefined}
                    wipBannerOffset={'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined}
                    isEditing={isCurrentSlide && isEditing}
                    onUpdate={(updates) => isCurrentSlide && nodeOps.updateNodeData(node.id, updates)}
                  />
                ) : node.type === 'iframe' ? (
                  <IframeNode
                    id={node.id}
                    src={node.data.content}
                    title={'title' in node.data ? node.data.title : undefined}
                    frameStyle={node.data.frameStyle as 'browser' | 'none'}
                    borderRadius={'borderRadius' in node.data ? node.data.borderRadius : undefined}
                    workInProgress={'workInProgress' in node.data ? node.data.workInProgress : undefined}
                    wipBannerOffset={'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined}
                    iframeZoom={'iframeZoom' in node.data ? node.data.iframeZoom : undefined}
                    isEditing={isCurrentSlide && isEditing}
                    onTitleChange={(title) => isCurrentSlide && nodeOps.updateNodeData(node.id, { title })}
                    onUpdate={(updates) => isCurrentSlide && nodeOps.updateNodeData(node.id, updates)}
                  />
                ) : (
                  <ImageNode
                    id={node.id}
                    type={node.type as 'image' | 'video'}
                    src={node.data.content}
                    title={'title' in node.data ? node.data.title : undefined}
                    frameStyle={node.data.frameStyle as 'browser' | 'none'}
                    isEditing={isCurrentSlide && isEditing}
                    workInProgress={'workInProgress' in node.data ? node.data.workInProgress : undefined}
                    wipBannerOffset={'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined}
                    borderRadius={'borderRadius' in node.data ? node.data.borderRadius : undefined}
                    onTitleChange={(title) => isCurrentSlide && nodeOps.updateNodeData(node.id, { title })}
                    onUpdate={(updates) => isCurrentSlide && nodeOps.updateNodeData(node.id, updates)}
                  />
                )}
              </NodeWrapper>
            ))}
          </SlideContainer>
        );
      })}
    </div>
  );
};
